import { ROLE_LABELS } from '@/modules/app/MyUserSchema';
import { Reference, TypePolicies } from '@apollo/client';
import {
  Counter,
  CounterSource,
  CounterType,
  Machine,
  MachineCounter,
  MachineDayReportSummary,
  MachineFieldsFragmentDoc,
  OtherProjectsStats,
  ProjectStats,
  RoleNames,
} from '@/generated/codegen';

const displayCounters = (counters?: readonly Counter[]) =>
  counters
    ?.map((counter) => `${counter.value.toFixed(1)}${counter.type}`)
    .join(', ')
    .replace(/^$/, '-') ?? '-';

export const localFields: TypePolicies = {
  FuelProvider: {
    fields: {
      last_known_flowmeter: () => 0,
    },
  },
  ProjectStats: {
    fields: {
      num_machines: (existingData, { readField }) => {
        const machines_stats = readField(
          'machines_stats'
        ) as ProjectStats['machines_stats'];
        return (
          machines_stats.num_vehicles +
          machines_stats.num_trucks +
          machines_stats.num_heavy_equipments +
          machines_stats.num_fixed_assets
        );
      },
    },
  },
  OtherProjectsStats: {
    fields: {
      num_machines: (existingData, { readField }) => {
        const machines_stats = readField(
          'machines_stats'
        ) as OtherProjectsStats['machines_stats'];
        return (
          machines_stats.num_vehicles +
          machines_stats.num_trucks +
          machines_stats.num_heavy_equipments +
          machines_stats.num_fixed_assets
        );
      },
    },
  },
  MachineReportsBoard: {
    fields: {
      reports: { keyArgs: false },
      stats: { keyArgs: false },
      total_days_with_reports_hs: (existingData, { readField }) => {
        const reports = readField('reports') as MachineDayReportSummary[];
        return (
          reports?.reduce(
            (total, report) =>
              readField<Counter[]>('total_counters', report)?.find(
                (counter) => counter.type === CounterType.Hs
              )
                ? total + 1
                : total,
            0
          ) ?? 0
        );
      },
      total_days_with_reports_km: (existingData, { readField }) => {
        const reports = readField('reports') as MachineDayReportSummary[];
        return (
          reports?.reduce(
            (total, report) =>
              readField<Counter[]>('total_counters', report)?.find(
                (counter) => counter.type === CounterType.Km
              )
                ? total + 1
                : total,
            0
          ) ?? 0
        );
      },
    },
  },
  MachineDayReportSummary: {
    keyFields: (summary) =>
      `MachineDayReportSummary:${summary.id ?? summary.date}`,
    fields: {
      has_photos: (existingData, { readField }) => !!readField('num_photos'),
      total_counters_display: (existingData, { readField }) =>
        displayCounters(readField<Counter[]>('total_counters')),
    },
  },
  MachinistDayWorkSummary: {
    fields: {
      total_counters_display: (existingData, { readField }) =>
        displayCounters(readField<Counter[]>('total_counters')),
    },
  },
  MachineReportsStats: {
    fields: {
      total_hs: (existingData, { readField }) => {
        const totals = readField('total_counters') as Counter[];
        return (
          totals.find((total) => total.type === CounterType.Hs)?.value ?? 0
        );
      },
      total_km: (existingData, { readField }) => {
        const totals = readField('total_counters') as Counter[];
        return (
          totals.find((total) => total.type === CounterType.Km)?.value ?? 0
        );
      },
      total_counters_display: (existingData, { readField }) =>
        displayCounters(readField<Counter[]>('total_counters')),
    },
  },
  MachinistWorkBoard: {
    fields: {
      summaries: {
        keyArgs: false,
      },
      rate_reported: (existingData, { readField }) => {
        const summaries = readField('summaries') as readonly Reference[];
        const reported =
          summaries?.reduce(
            (total, summary) =>
              readField('has_reports', summary) ? total + 1 : total,
            0
          ) ?? 0;
        return summaries.length ? reported / summaries.length : 0;
      },
    },
  },
  User: {
    fields: {
      full_name: (existingData, { readField }) =>
        `${readField('last_name')}, ${readField('first_name')}`,
    },
  },
  Employee: {
    fields: {
      full_name: (existingData, { readField }) =>
        `${readField('last_name')}, ${readField('first_name')}`,
    },
  },
  Role: {
    fields: {
      label: (existingData, { readField }) => {
        return ROLE_LABELS[readField('role') as RoleNames] ?? readField('role');
      },
    },
  },
  Invitation: {
    fields: {
      label: (existingData, { readField }) => {
        return ROLE_LABELS[readField('role') as RoleNames] ?? readField('role');
      },
    },
  },
  DailyReport: {
    fields: {
      total_counters_display: (existingData, { readField }) =>
        displayCounters(readField<Counter[]>('total_counters')),
      total_hs: (existingData, { readField }) => {
        const totals = readField('total_counters') as Counter[];
        return (
          totals.find((total) => total.type === CounterType.Hs)?.value ?? 0
        );
      },
      total_km: (existingData, { readField }) => {
        const totals = readField('total_counters') as Counter[];
        return (
          totals.find((total) => total.type === CounterType.Km)?.value ?? 0
        );
      },
      total_fuel: (existingData, { readField }) =>
        (readField('usage_reports') as readonly Reference[])?.reduce(
          (total, usage_report) =>
            total + Number(readField('fuel', usage_report)),
          0
        ),
      total_oil: (existingData, { readField }) =>
        (readField('usage_reports') as readonly Reference[])?.reduce(
          (total, usage_report) =>
            total + Number(readField('motor_oil', usage_report)),
          0
        ),
      creators: (existingData, { readField }) =>
        (readField('usage_reports') as readonly Reference[])?.map(
          (usage_report) => readField('creator', usage_report)
        ),
      has_grease: (existingData, { readField }) =>
        !!(readField('usage_reports') as readonly Reference[])?.find(
          (usage_report) => !!readField('grease', usage_report)
        ),
      has_filters: (existingData, { readField }) =>
        !!(readField('usage_reports') as readonly Reference[])?.find(
          (usage_report) => !!readField('filters', usage_report)
        ),
      has_comments: (existingData, { readField }) =>
        !!(readField('usage_reports') as readonly Reference[])?.find(
          (usage_report) => !!readField('comments', usage_report)
        ),
      has_tasks: (existingData, { readField }) =>
        !!(readField('usage_reports') as readonly Reference[])?.find(
          (usage_report) => !!readField('tasks', usage_report)
        ),
    },
  },
  Machine: {
    fields: {
      type: {
        merge(existing, incoming, { readField }) {
          if (!existing || !incoming) {
            return incoming;
          }

          const existingTypeId = readField('id', existing);
          const incomingTypeId = readField('id', incoming);
          const existingSubtype = readField('subtype', existing);
          const incomingSubtype = readField('subtype', incoming);

          if (
            existingTypeId === incomingTypeId &&
            existingSubtype !== undefined &&
            incomingSubtype == null
          ) {
            return existing;
          }

          return incoming;
        },
      },
      last_counter_from_usage_report: (existingData, { readField }) =>
        readField<MachineCounter[]>('last_counters')?.find(
          (lc) => lc.source === CounterSource.UsageReport
        ) ?? null,
      last_counter_from_fuel_report: (existingData, { readField }) =>
        readField<MachineCounter[]>('last_counters')?.find(
          (lc) => lc.source === CounterSource.FuelReport
        ) ?? null,
      display_type: (existingData, { readField }) =>
        readField('type', readField('type')),
      display_name: (existingData, { readField, cache }) => {
        const displayNameFields = JSON.parse(
          window.localStorage.getItem('Bilderit-machine-display-name') ??
            '["name", "brand", "model"]'
        ) as string[];
        if (
          displayNameFields.reduce(
            (result, field) => result && readField(field) !== undefined,
            true
          )
        ) {
          // we have all the required fields in the entity, make the display name
          return displayNameFields?.map((field) => readField(field)).join(' ');
        } else {
          // TODO: I think this is not necessary anymore
          // maybe I didnt request the necessary fields in this query, lets get them from cache
          try {
            const machineId = `Machine:${readField('id')}`;
            const machine = cache.readFragment({
              fragment: MachineFieldsFragmentDoc,
              id: machineId,
            }) as Machine;
            const displayNameString =
              displayNameFields
                ?.map((field) => machine[field as keyof Machine] ?? '')
                ?.join(' ') ?? machine?.name;
            return displayNameString;
          } catch {
            return readField('name');
          }
        }
      },
    },
  },
  // El id es el chequeo (`DATA_MATCHES`…), igual en todos los usuarios:
  // normalizado, la ficha de uno pisaría los chequeos de otro.
  AdminUserCheck: { keyFields: false },
  MachineType: {
    keyFields: (summary) =>
      `MachineType:${summary.id}${
        summary.subtype ? ':' + summary.subtype : ''
      }`,
  },
  MaintenancePlan: {
    keyFields: (summary) =>
      `MaintenancePlan:${summary.id}${
        summary.id === -1 ? ':' + summary.base_period : ''
      }`,
  },
  // MaintenancePlan: {
  //   keyFields: ['id', 'base_period'],
  // },
};
